/**
 * Utility functions for applying branding to the application
 */

/**
 * Converts a hex color to RGB values
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Converts RGB values to hex color
 */
function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('');
}

/**
 * Lightens a color by a percentage
 */
function lightenColor(hex: string, percent: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const r = Math.min(255, Math.round(rgb.r + (255 - rgb.r) * (percent / 100)));
  const g = Math.min(255, Math.round(rgb.g + (255 - rgb.g) * (percent / 100)));
  const b = Math.min(255, Math.round(rgb.b + (255 - rgb.b) * (percent / 100)));

  return rgbToHex(r, g, b);
}

/**
 * Darkens a color by a percentage
 */
function darkenColor(hex: string, percent: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const r = Math.max(0, Math.round(rgb.r * (1 - percent / 100)));
  const g = Math.max(0, Math.round(rgb.g * (1 - percent / 100)));
  const b = Math.max(0, Math.round(rgb.b * (1 - percent / 100)));

  return rgbToHex(r, g, b);
}

/**
 * Gets the appropriate foreground color (white or black) based on background brightness
 */
function getForegroundColor(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return '#FFFFFF';

  // Calculate relative luminance
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.5 ? '#09090B' : '#FFFFFF';
}

/**
 * Generates color variations (main, light, dark) from a base color
 * @param baseColor - Base color in hex format
 * @param isDark - Whether dark mode is active
 * @returns Object with main, light, dark, and contrastText properties
 */
export function generateColorVariations(
  baseColor: string,
  isDark: boolean
): { main: string; light: string; dark: string; contrastText: string } {
  if (isDark) {
    // In dark mode, use lighter variations
    return {
      main: baseColor,
      light: lightenColor(baseColor, 20),
      dark: darkenColor(baseColor, 15),
      contrastText: getForegroundColor(baseColor),
    };
  } else {
    // In light mode, use standard variations
    return {
      main: baseColor,
      light: lightenColor(baseColor, 30),
      dark: darkenColor(baseColor, 20),
      contrastText: getForegroundColor(baseColor),
    };
  }
}

/**
 * Adds alpha transparency to a hex color
 * @param hex - Hex color string (e.g., '#631760')
 * @param alpha - Alpha value between 0 and 1
 * @returns RGBA color string
 */
export function addAlpha(hex: string, alpha: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  // Clamp alpha between 0 and 1
  const clampedAlpha = Math.max(0, Math.min(1, alpha));

  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${clampedAlpha})`;
}

/**
 * Applies branding CSS variables to the document root
 * @param primaryColor - Primary brand color (hex)
 * @param secondaryColor - Secondary brand color (hex)
 * @param isDark - Whether dark mode is active
 */
export function applyBrandingCSS(
  primaryColor: string,
  secondaryColor: string,
  isDark: boolean
): void {
  const root = document.documentElement;

  if (isDark) {
    // Dark mode color variations
    const primarySoft = darkenColor(primaryColor, 85);
    const primaryAlpha = darkenColor(primaryColor, 70);
    const primaryAccent = lightenColor(primaryColor, 10);
    
    const secondarySoft = darkenColor(secondaryColor, 85);
    const secondaryAccent = lightenColor(secondaryColor, 10);

    // Apply primary color variables
    root.style.setProperty('--color-primary', secondaryColor); // Use secondary as primary in dark mode
    root.style.setProperty('--color-primary-foreground', getForegroundColor(secondaryColor));
    root.style.setProperty('--color-primary-soft', primarySoft);
    root.style.setProperty('--color-primary-alpha', primaryAlpha);
    root.style.setProperty('--color-primary-accent', primaryAccent);

    // Apply secondary color variables
    root.style.setProperty('--color-secondary', secondaryAccent);
    root.style.setProperty('--secondary', secondaryAccent);
    root.style.setProperty('--color-secondary-foreground', getForegroundColor(secondaryAccent));
    root.style.setProperty('--secondary-foreground', getForegroundColor(secondaryAccent));
    root.style.setProperty('--color-secondary-soft', secondarySoft);

    // Radix UI compatible variables
    root.style.setProperty('--primary', secondaryColor);
    root.style.setProperty('--primary-foreground', getForegroundColor(secondaryColor));
    root.style.setProperty('--ring', secondaryColor);

    // Badge colors
    root.style.setProperty('--color-blue-700', secondaryColor);
    root.style.setProperty('--color-blue-50', primarySoft);
    root.style.setProperty('--color-blue-600', secondaryAccent);
  } else {
    // Light mode color variations
    const primarySoft = lightenColor(primaryColor, 95);
    const primaryAlpha = lightenColor(primaryColor, 85);
    const primaryAccent = primaryColor;

    const secondarySoft = lightenColor(secondaryColor, 95);

    // Apply primary color variables
    root.style.setProperty('--color-primary', primaryColor);
    root.style.setProperty('--color-primary-foreground', getForegroundColor(primaryColor));
    root.style.setProperty('--color-primary-soft', primarySoft);
    root.style.setProperty('--color-primary-alpha', primaryAlpha);
    root.style.setProperty('--color-primary-accent', primaryAccent);

    // Apply secondary color variables
    root.style.setProperty('--color-secondary', secondaryColor);
    root.style.setProperty('--secondary', secondaryColor);
    root.style.setProperty('--color-secondary-foreground', getForegroundColor(secondaryColor));
    root.style.setProperty('--secondary-foreground', getForegroundColor(secondaryColor));
    root.style.setProperty('--color-secondary-soft', secondarySoft);

    // Radix UI compatible variables
    root.style.setProperty('--primary', primaryColor);
    root.style.setProperty('--primary-foreground', getForegroundColor(primaryColor));
    root.style.setProperty('--ring', primaryColor);

    // Badge colors
    root.style.setProperty('--color-blue-700', primaryColor);
    root.style.setProperty('--color-blue-50', primarySoft);
    root.style.setProperty('--color-blue-600', secondaryColor);
    root.style.setProperty('--color-blue-950', primaryColor);
  }
}

/**
 * Removes branding CSS variables from the document root
 * allowing the application to fallback to default index.css variables
 */
export function removeBrandingCSS(): void {
  const root = document.documentElement;
  
  const propertiesToClear = [
    '--color-primary', '--color-primary-foreground', '--color-primary-soft', 
    '--color-primary-alpha', '--color-primary-accent',
    '--color-secondary', '--color-secondary-foreground', '--color-secondary-soft',
    '--primary', '--primary-foreground', '--ring',
    '--color-blue-700', '--color-blue-50', '--color-blue-600'
  ];

  propertiesToClear.forEach(prop => {
    root.style.removeProperty(prop);
  });
}

/**
 * Updates the favicon of the page
 * @param faviconUrl - URL of the favicon to set, or null to restore default
 */
export function updateFavicon(faviconUrl: string | null): void {
  // Remove existing favicon links
  const existingLinks = document.querySelectorAll("link[rel*='icon']");
  existingLinks.forEach((link) => link.remove());

  if (faviconUrl) {
    // Set custom favicon
    const link = document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/x-icon';
    link.href = faviconUrl;
    document.head.appendChild(link);
  } else {
    // Restore default favicon
    const defaultFavicon = document.createElement('link');
    defaultFavicon.rel = 'icon';
    defaultFavicon.type = 'image/svg+xml';
    defaultFavicon.href = `/favicon.ico?v=${Date.now()}`;
    document.head.appendChild(defaultFavicon);
  }
}
